import React, { useState, useEffect, useCallback } from 'react';
import Content from '../../components/Content';
import NetworkError from '../../components/Error';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';
import styles from './SwitchCAM.module.css'; // We'll create this for styling

const FRONTEND_SWITCH_CAM_ERROR_CODES = {
    INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
};

const buildSwitchCamSupportError = (errorCode, pageInfo, sourceMessage) =>
    formatFrontendSupportError({
        errorCode,
        pageInfo,
        sourceMessage,
    });

const SwitchCAM = () => {
    const [camData, setCamData] = useState([]);
    const [filteredCamData, setFilteredCamData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCompany, setSelectedCompany] = useState('ALL');
    const [availableCompanies, setAvailableCompanies] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [supportErrorMessage, setSupportErrorMessage] = useState('');
    const [copiedSupportError, setCopiedSupportError] = useState(false);

    const setSwitchCamSupportError = useCallback((errorCode, pageInfo, sourceMessage) => {
        setSupportErrorMessage(buildSwitchCamSupportError(errorCode, pageInfo, sourceMessage));
        setCopiedSupportError(false);
    }, []);

    const clearSwitchCamSupportError = useCallback((force = false) => {
        if (!force) return;
        setSupportErrorMessage('');
        setCopiedSupportError(false);
    }, []);

    const handleCopySupportError = useCallback(async () => {
        if (!supportErrorMessage) return;
        try {
            await navigator.clipboard.writeText(supportErrorMessage);
            setCopiedSupportError(true);
            setTimeout(() => setCopiedSupportError(false), 2000);
        } catch (copyError) {
            setCopiedSupportError(false);
        }
    }, [supportErrorMessage]);

    // Create a simple action for fetching CAM data
    const getCamData = useCallback(async (companyFilter = 'ALL') => {
        try {
            let url = '/api/cam_switches';
            if (companyFilter !== 'ALL') {
                url += `?company=${companyFilter}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            let data = {};
            try {
                data = await response.json();
            } catch (parseError) {
                data = {};
            }

            if (!response.ok) {
                return {
                    status: false,
                    msg: data?.msg || `Switch CAM request failed with HTTP ${response.status}`,
                };
            }

            return data;
        } catch (error) {
            console.error('Error fetching CAM data:', error);
            return { status: false, msg: error.message };
        }
    }, []);

    const fetchData = useCallback(async (companyFilter = 'ALL') => {
        setIsLoading(true);
        setError(null);
        
        const data = await getCamData(companyFilter);
        
        if (data && data.status) {
            setColumns(data.CamCols || []);
            setCamData(data.CamRows || []);
            setFilteredCamData(data.CamRows || []);
            setAvailableCompanies(data.availableCompanies || []);
            clearSwitchCamSupportError(true);
        } else {
            const sourceMessage = data?.msg || 'Failed to fetch CAM data.';
            setError(sourceMessage);
            setSwitchCamSupportError(
                FRONTEND_SWITCH_CAM_ERROR_CODES.INITIAL_LOAD_FAILED,
                'Switch CAM Page / CAM Data Load Failed',
                sourceMessage
            );
        }
        
        setIsLoading(false);
    }, [clearSwitchCamSupportError, getCamData, setSwitchCamSupportError]);

    // Filter function for search
    const filterData = useCallback((data, searchTerm) => {
        if (!searchTerm.trim()) {
            return data;
        }

        const lowercaseSearch = searchTerm.toLowerCase();
        return data.filter(row => 
            row.some(cell => {
                if (cell === null || cell === undefined) return false;
                return String(cell).toLowerCase().includes(lowercaseSearch);
            })
        );
    }, []);

    // Effect to handle search filtering
    useEffect(() => {
        const filtered = filterData(camData, searchTerm);
        setFilteredCamData(filtered);
    }, [camData, searchTerm, filterData]);

    useEffect(() => {
        fetchData(selectedCompany);
    }, [fetchData, selectedCompany]);

    const handleCompanyChange = (event) => {
        const newCompany = event.target.value;
        setSelectedCompany(newCompany);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const clearSearch = () => {
        setSearchTerm('');
    };

    if (isLoading) {
        return (
            <Content currentMenu='switchcam' headerTitle='Switch CAM'>
                <div className={styles.loading}>Loading CAM data...</div>
            </Content>
        );
    }

    if (error) {
        return (
            <Content currentMenu='switchcam' headerTitle='Switch CAM'>
                <NetworkError />
                <div className={styles.supportErrorCard}>
                    <strong>Support Error:</strong>
                    <code className={styles.supportErrorCode}>{supportErrorMessage || error}</code>
                    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                        <button
                            type='button'
                            className={styles.copySupportErrorButton}
                            onClick={handleCopySupportError}
                        >
                            {copiedSupportError ? 'Copied' : 'Copy Error Details'}
                        </button>
                        <button
                            type='button'
                            className={styles.copySupportErrorButton}
                            onClick={() => clearSwitchCamSupportError(true)}
                        >
                            Clear
                        </button>
                    </div>
                </div>
            </Content>
        );
    }

    return (
        <Content currentMenu='switchcam' headerTitle='Switch CAM'>
            <div className={styles.switchCamContainer}>
                {supportErrorMessage && (
                    <div className={styles.supportErrorBanner}>
                        <div className={styles.supportErrorBannerText}>
                            <strong>Support Error:</strong>
                            <code className={styles.supportErrorCode}>{supportErrorMessage}</code>
                        </div>
                        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                            <button
                                type='button'
                                className={styles.copySupportErrorButton}
                                onClick={handleCopySupportError}
                            >
                                {copiedSupportError ? 'Copied' : 'Copy Error Details'}
                            </button>
                            <button
                                type='button'
                                className={styles.copySupportErrorButton}
                                onClick={() => clearSwitchCamSupportError(true)}
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                )}
                <div className={styles.controlsContainer}>
                    {availableCompanies.length > 0 && (
                        <div className={styles.filterContainer}>
                            <label htmlFor="companyFilter">Filter by Company: </label>
                            <select id="companyFilter" value={selectedCompany} onChange={handleCompanyChange}>
                                <option value="ALL">All Companies</option>
                                {availableCompanies.map(company => (
                                    <option key={company} value={company}>{company}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className={styles.searchContainer}>
                        <label htmlFor="searchInput">Search: </label>
                        <div className={styles.searchInputWrapper}>
                            <input
                                id="searchInput"
                                type="text"
                                placeholder="Search by MAC address, switch name, VLAN, interface..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className={styles.searchInput}
                            />
                            {searchTerm && (
                                <button
                                    onClick={clearSearch}
                                    className={styles.clearSearchButton}
                                    type="button"
                                    aria-label="Clear search"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {searchTerm && (
                    <div className={styles.searchInfo}>
                        Showing {filteredCamData.length} of {camData.length} entries 
                        {filteredCamData.length !== camData.length && ` matching "${searchTerm}"`}
                    </div>
                )}

                {filteredCamData.length === 0 ? (
                    <p>
                        {searchTerm 
                            ? `No CAM data found matching "${searchTerm}" for the selected company.`
                            : "No CAM data available for the selected company."
                        }
                    </p>
                ) : (
                    <table className={styles.camTable}>
                        <thead>
                            <tr>
                                {columns.map(col => <th key={col}>{col}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCamData.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {row.map((cell, cellIndex) => (
                                        <td key={`${rowIndex}-${cellIndex}`}>
                                            {cell === null || cell === undefined ? 'N/A' : String(cell)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </Content>
    );
};

export default SwitchCAM;
